// src/components/jarvislyComponents/ValidatedInput.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useRef } from 'react';
import { Input, Space } from 'antd';
import ValidationSuffix from './ValidationSuffix';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ValidatedInput = React.forwardRef(
  (
    {
      // do not forward props that toggle AntD suffix dynamically
      allowClear, // avoid suffix mount/unmount by AntD
      prefix,
      suffix,
      status, // '', 'validating', 'success', 'error'
      loading, // boolean
      type,
      addonBefore, // deprecated in antd v5 - will handle with Space.Compact
      addonAfter,  // deprecated in antd v5 - will handle with Space.Compact
      // filter out custom props that should not reach the DOM
      elementProps,
      noLabel,
      ...props
    },
    ref
  ) => {
    // ─────────────────────────────────────────────────────────────────────────────
    // 1. PROPS, CONTEXTS & GLOBAL STATE
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 2. HOOKS & REFS
    // ─────────────────────────────────────────────────────────────────────────────
    // keep prefix stable to avoid DOM restructure / focus loss
    const stablePrefixRef = useRef(prefix ?? null);

    // ─────────────────────────────────────────────────────────────────────────────
    // 3. LOCAL VARIABLES I
    // ─────────────────────────────────────────────────────────────────────────────
    const staticSuffix = useMemo(() => {
      return <ValidationSuffix status={status} loading={loading} extraSuffix={suffix} />;
    }, [status, loading, suffix]);

    // ─────────────────────────────────────────────────────────────────────────────
    // 4. LOCAL STATE
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 5. EFFECTS
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 6. HANDLERS
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 7. LOCAL VARIABLES II
    // ─────────────────────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────────────────────
    // 8. UI
    // ─────────────────────────────────────────────────────────────────────────────
    // Use Space.Compact if addonBefore or addonAfter are provided
    if (addonBefore || addonAfter) {
      return (
        <Space.Compact style={{ width: '100%' }} block>
          {addonBefore}
          <Input
            {...props}
            ref={ref}
            type={type}
            prefix={stablePrefixRef.current}
            suffix={staticSuffix}
            style={{ width: '100%' }}
            // IMPORTANT: do not pass allowClear to avoid dynamic suffix from AntD
          />
          {addonAfter}
        </Space.Compact>
      );
    }

    // Regular input without addons
    return (
      <Input
        {...props}
        ref={ref}
        type={type}
        prefix={stablePrefixRef.current}
        suffix={staticSuffix}
        // IMPORTANT: do not pass allowClear to avoid dynamic suffix from AntD
      />
    );
  }
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ValidatedInput;

// *******************************************************************************
// HELPERS
// *******************************************************************************
